import { type FC } from "react";
import { Box, Button, Stack, TextField } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import TagFilterSelect from "../../../components/tagSelect/TagFilterSelect";
import { useAgents } from "../../../admin/agents/useAgents";
import { CUSTOMER_STATUS_VALUES } from "../../customerStatus";
import type { CustomerStatus } from "../../customersApi";
import { useCustomerTagsByCategory } from "../../useCustomerTagsByCategory";
import { POLISH_VOIVODESHIPS } from "../../voivodeships";
import type { CustomerHasFilter, MeetingType } from "@przezmorze/api-types";
import { type ClientFilters, MEETING_TYPE_LABELS } from "../clientFilters";
import FilterSelectField, { type FilterSelectOption } from "./FilterSelectField";
import FilterDateField from "./FilterDateField";
import PredefinedFilterChips from "./PredefinedFilterChips";

interface Props {
  filters: ClientFilters;
  onChange: (patch: Partial<ClientFilters>) => void;
  onApply: () => void;
  onReset: () => void;
  onAddPredefinedFilter: (value: CustomerHasFilter) => void;
}

const toOptions = (values: readonly string[]): FilterSelectOption[] => values.map((value) => ({ value, label: value }));

const statusOptions = toOptions(CUSTOMER_STATUS_VALUES);
const voivodeshipOptions = toOptions(POLISH_VOIVODESHIPS);
const meetingTypeOptions = Object.entries(MEETING_TYPE_LABELS).map(([value, label]) => ({ value, label }));

const ClientFiltersForm: FC<Props> = ({ filters, onChange, onApply, onReset, onAddPredefinedFilter }) => {
  const { agents } = useAgents();
  const customerTagsByCategory = useCustomerTagsByCategory();
  const accountManagerOptions = agents.map((agent) => ({ value: agent.id, label: agent.name }));

  return (
    <Box
      sx={{
        p: 2,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
        gap: 2,
      }}
    >
      <TextField
        size="small"
        label="Szukaj"
        placeholder="Nazwa, telefon, e-mail…"
        value={filters.query}
        onChange={(event) => onChange({ query: event.target.value })}
        sx={{ gridColumn: { xs: "1", md: "1 / 3" } }}
      />
      <FilterSelectField
        label="Status"
        value={filters.status}
        options={statusOptions}
        onChange={(status) => onChange({ status: status as CustomerStatus | "" })}
      />
      <FilterSelectField
        label="Status TravelPay"
        value={filters.travelPayStatus}
        options={statusOptions}
        onChange={(travelPayStatus) => onChange({ travelPayStatus: travelPayStatus as CustomerStatus | "" })}
      />
      <FilterSelectField
        label="Opiekun"
        value={filters.accountManagerId}
        options={accountManagerOptions}
        emptyLabel="Wszyscy"
        onChange={(accountManagerId) => onChange({ accountManagerId })}
      />
      <FilterSelectField
        label="Województwo"
        value={filters.voivodeship}
        options={voivodeshipOptions}
        onChange={(voivodeship) => onChange({ voivodeship })}
      />
      <TextField
        size="small"
        label="Miasto"
        value={filters.city}
        onChange={(event) => onChange({ city: event.target.value })}
      />
      <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1", md: "span 3" } }}>
        <TagFilterSelect
          label="Tagi"
          value={{ included: filters.tags, excluded: filters.excludedTags }}
          availableTags={customerTagsByCategory}
          maxVisibleTags={4}
          onChange={({ included, excluded }) => onChange({ tags: included, excludedTags: excluded })}
        />
      </Box>
      <FilterDateField
        label="Kalkulacja od"
        helperText="Klienci z kalkulacją > 0 po dacie"
        value={filters.calculationFrom}
        onChange={(calculationFrom) => onChange({ calculationFrom })}
      />
      <FilterDateField
        label="Zamówienie od"
        helperText="Klienci z zamówieniem > 0 po dacie"
        value={filters.orderFrom}
        onChange={(orderFrom) => onChange({ orderFrom })}
      />
      <FilterSelectField
        label="Spotkanie"
        value={filters.meetingType}
        options={meetingTypeOptions}
        onChange={(meetingType) => onChange({ meetingType: meetingType as MeetingType | "" })}
      />
      <FilterDateField
        label="Spotkanie od"
        value={filters.meetingFrom}
        onChange={(meetingFrom) => onChange({ meetingFrom })}
      />
      <FilterDateField
        label="Spotkanie do"
        value={filters.meetingTo}
        onChange={(meetingTo) => onChange({ meetingTo })}
      />
      <FilterDateField
        label="Notatka Przezmorze od"
        value={filters.notePrzezmorzeFrom}
        onChange={(notePrzezmorzeFrom) => onChange({ notePrzezmorzeFrom })}
      />
      <FilterDateField
        label="Notatka Przezmorze do"
        value={filters.notePrzezmorzeTo}
        onChange={(notePrzezmorzeTo) => onChange({ notePrzezmorzeTo })}
      />
      <FilterDateField
        label="Notatka TravelPay od"
        value={filters.noteTravelPayFrom}
        onChange={(noteTravelPayFrom) => onChange({ noteTravelPayFrom })}
      />
      <FilterDateField
        label="Notatka TravelPay do"
        value={filters.noteTravelPayTo}
        onChange={(noteTravelPayTo) => onChange({ noteTravelPayTo })}
      />
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{ gridColumn: "1 / -1", alignItems: "center", flexWrap: "wrap" }}
      >
        <Button variant="contained" startIcon={<FilterListIcon />} onClick={onApply}>
          Filtruj
        </Button>
        <Button variant="text" onClick={onReset}>
          Wyczyść
        </Button>
        <PredefinedFilterChips applied={filters.has} onAdd={onAddPredefinedFilter} />
      </Stack>
    </Box>
  );
};

export default ClientFiltersForm;
